
import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import customerService, { Customer } from '../services/customerService';
import { Search, RefreshCcw, Printer, Eye, Edit, UserPlus } from 'lucide-react';
import { SaleDetailModal } from '../components/SaleDetailModal';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/format';
import saleService, { Sale } from '../services/saleService';
import { TicketOptionsModal } from '../components/TicketOptionsModal';
import { printTicketWindow, sendTicketWhatsApp } from '../utils/ticketUtils';
import { EditCustomerModal } from '../components/customers/EditCustomerModal';

export const CustomersPage: React.FC = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Pagination
    const [total, setTotal] = useState(0);
    const [limit] = useState(50);
    const [offset, setOffset] = useState(0);

    // Modal para reimprimir recibos
    const [showReceiptsModal, setShowReceiptsModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [customerSales, setCustomerSales] = useState<any[]>([]);
    const [showSaleDetailModal, setShowSaleDetailModal] = useState(false);
    const [selectedSaleDetail, setSelectedSaleDetail] = useState<any>(null);

    // Estado para el modal de opciones de ticket (Reimpresión)
    const [showTicketOptionsModal, setShowTicketOptionsModal] = useState(false);
    const [selectedSaleForTicket, setSelectedSaleForTicket] = useState<Sale | null>(null);

    // Edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setOffset(0); // Reset pagination on search
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        loadCustomers();
    }, [debouncedSearch, offset]);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const data = await customerService.getCustomers({
                limit,
                offset,
                search: debouncedSearch
            });
            setCustomers(data.data);
            setTotal(data.total);
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    };

    const handleViewReceipts = async (customer: Customer) => {
        try {
            setLoading(true);
            // Fetch full details including sales
            const details = await customerService.getCustomerById(customer.id);
            setSelectedCustomer(details);
            setCustomerSales(details.sales || []);
            setShowReceiptsModal(true);
        } catch (error) {
            toast.error('Error al cargar historial del cliente');
        } finally {
            setLoading(false);
        }
    };

    const calculateTotalPurchases = (sales: any[]) => {
        return sales.reduce((acc, sale) => acc + (sale.total || 0), 0);
    };

    const handlePrintTicket = async (phone: string) => {
        if (!selectedSaleForTicket) return;
        try {
            setLoading(true);
            // Get fresh data to ensure we have all items populated correctly
            const saleResponse = await saleService.getSaleById(selectedSaleForTicket.id);
            printTicketWindow({ ...saleResponse.sale, customerPhone: phone });
        } catch (error) {
            console.error(error);
            toast.error('Error al imprimir');
        } finally {
            setLoading(false);
        }
    };

    const handleWhatsAppTicket = async (phone: string) => {
        if (!selectedSaleForTicket) return;
        try {
            setLoading(true);
            const saleResponse = await saleService.getSaleById(selectedSaleForTicket.id);
            await sendTicketWhatsApp({ ...saleResponse.sale, customerPhone: phone });

            setShowTicketOptionsModal(false);
            setSelectedSaleForTicket(null);
        } catch (error) {
            console.error(error);
            toast.error('Error al enviar WhatsApp');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Layout>
            <div className="min-h-screen bg-gray-50">
                <div className="bg-white border-b border-gray-200 px-6 py-4">
                    <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
                </div>

                <div className="p-6">
                    {/* Top Actions */}
                    <div className="mb-6 flex justify-between items-center">
                        <div className="relative w-full max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Buscar por nombre o teléfono..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <button
                            onClick={() => {
                                setCustomerToEdit({ id: 0, fullName: '', phone: '', email: '', type: 'NORMAL' } as Customer);
                                setShowEditModal(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-sm whitespace-nowrap ml-4"
                        >
                            <UserPlus size={20} />
                            Hacer Registro
                        </button>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono / WhatsApp</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Compras</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Registro</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr><td colSpan={6} className="text-center py-4">Cargando...</td></tr>
                                ) : (
                                    customers.map((customer) => (
                                        <tr key={customer.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                                                        {customer.fullName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">{customer.fullName}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {customer.phone || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {customer.email || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                {customer.totalSales || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(customer.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setCustomerToEdit(customer);
                                                            setShowEditModal(true);
                                                        }}
                                                        className="text-gray-500 hover:text-blue-600 flex items-center gap-1"
                                                    >
                                                        <Edit size={16} /> Editar
                                                    </button>
                                                    <button
                                                        onClick={() => handleViewReceipts(customer)}
                                                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                                    >
                                                        <RefreshCcw size={16} /> Historial
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls could go here */}
                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-right text-sm text-gray-600">
                            Total: {total} clientes
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Historial */}
            {showReceiptsModal && selectedCustomer && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowReceiptsModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">{selectedCustomer.fullName} - Historial</h2>
                                <p className="text-sm text-gray-600">Total Gastado: {formatCurrency(calculateTotalPurchases(customerSales))}</p>
                            </div>
                            <button onClick={() => setShowReceiptsModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            {customerSales.length === 0 ? (
                                <p className="text-center text-gray-500 py-10">No hay compras registradas</p>
                            ) : (
                                <div className="space-y-4">
                                    {customerSales.map((sale) => (
                                        <div key={sale.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <p className="font-bold text-gray-900">Venta: {sale.folio || sale.id}</p>
                                                    <p className="text-sm text-gray-500">{new Date(sale.createdAt).toLocaleDateString()} {new Date(sale.createdAt).toLocaleTimeString()}</p>
                                                </div>
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${sale.status === 'COMPLETED' || sale.status === 'COMPLETADA'
                                                    ? 'bg-green-100 text-green-700'
                                                    : sale.status === 'PRE-ORDER'
                                                        ? 'bg-yellow-100 text-yellow-700'
                                                        : sale.status === 'APARTADO'
                                                            ? 'bg-purple-100 text-purple-700'
                                                            : 'bg-yellow-100 text-yellow-700'
                                                    }`}>
                                                    {sale.status === 'PRE-ORDER' ? 'PRE-ORDER' : sale.status}
                                                </span>
                                            </div>

                                            <div className="bg-gray-50 p-3 rounded mb-3 text-sm">
                                                {(sale.sale_items || []).map((item: any, idx: number) => (
                                                    <div key={idx} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                                                        <span>{item.productName}</span>
                                                        <span>{formatCurrency(item.total)}</span>
                                                    </div>
                                                ))}
                                                <div className="flex justify-between pt-2 font-bold mt-2 border-t border-gray-200">
                                                    <span>Total</span>
                                                    <span>{formatCurrency(sale.total)}</span>
                                                </div>
                                            </div>

                                            <div className="flex justify-end gap-3">
                                                <button
                                                    onClick={() => {
                                                        setSelectedSaleForTicket(sale);
                                                        setShowTicketOptionsModal(true);
                                                    }}
                                                    className="text-blue-600 hover:bg-blue-50 px-3 py-1 rounded text-sm font-medium flex items-center gap-1"
                                                >
                                                    <Printer size={16} /> Reimprimir Recibo
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setSelectedSaleDetail(sale);
                                                        setShowSaleDetailModal(true);
                                                    }}
                                                    className="text-gray-600 hover:bg-gray-50 px-3 py-1 rounded text-sm font-medium flex items-center gap-1"
                                                >
                                                    <Eye size={16} /> Ver Detalle
                                                </button>

                                                {(sale.status === 'PENDING' || sale.status === 'PENDIENTE' || sale.status === 'PRE-ORDER') && (
                                                    <button
                                                        onClick={async () => {
                                                            if (window.confirm('¿Confirmar que esta venta ha sido completada y entregada? Esto cambiará el estado a COMPLETADA.')) {
                                                                try {
                                                                    setLoading(true);
                                                                    await saleService.updateSaleStatus(Number(sale.id), 'COMPLETADA');
                                                                    toast.success('Venta completada exitosamente');
                                                                    // Refresh
                                                                    const details = await customerService.getCustomerById(selectedCustomer.id);
                                                                    setCustomerSales(details.sales || []);
                                                                } catch (error) {
                                                                    console.error(error);
                                                                    toast.error('Error al actualizar estado');
                                                                } finally {
                                                                    setLoading(false);
                                                                }
                                                            }
                                                        }}
                                                        className="text-green-600 hover:bg-green-50 px-3 py-1 rounded text-sm font-medium flex items-center gap-1 border border-green-200"
                                                    >
                                                        <RefreshCcw size={16} /> Completar / Entregar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Detalle Venta */}
            {showSaleDetailModal && selectedSaleDetail && (
                <SaleDetailModal
                    sale={selectedSaleDetail}
                    onClose={() => {
                        setShowSaleDetailModal(false);
                        setSelectedSaleDetail(null);
                    }}
                />
            )}

            {/* Modal Opciones Ticket (Reimpresión) */}
            {showTicketOptionsModal && selectedSaleForTicket && (
                <TicketOptionsModal
                    sale={selectedSaleForTicket}
                    onPrint={(phone) => handlePrintTicket(phone)}
                    onWhatsApp={(phone) => handleWhatsAppTicket(phone)}
                    onClose={() => {
                        setShowTicketOptionsModal(false);
                        setSelectedSaleForTicket(null);
                    }}
                />
            )}

            {/* Modal de Edición de Cliente (Tipo MAYORISTA/NORMAL) */}
            {showEditModal && customerToEdit && (
                <EditCustomerModal
                    customer={customerToEdit}
                    onClose={() => {
                        setShowEditModal(false);
                        setCustomerToEdit(null);
                    }}
                    onSave={async (id, data) => {
                        try {
                            if (id === 0) {
                                await customerService.createCustomer(data);
                                toast.success('Cliente creado correctamente');
                            } else {
                                await customerService.updateCustomer(id, data);
                                toast.success('Cliente actualizado correctamente');
                            }
                            setShowEditModal(false);
                            setCustomerToEdit(null);
                            loadCustomers();
                        } catch (e: any) {
                            toast.error(e.response?.data?.message || 'Error al guardar cliente');
                        }
                    }}
                />
            )}
        </Layout>
    );
};
